import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import * as moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine-dark.css';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { RMDashboardService } from '../../services/rm-dashboard.service';
import { RMconfigurationService } from 'src/app/services/rmconfiguration.service';
import { TranslaterService } from 'src/app/services/translater.service';
import { UserConfigurationsService } from 'src/app/services/user-configurations.service';
import { BaseApi } from 'src/backend/api/base-api';
import { ApplicationPermission } from 'src/app/user-management/shared/models/application-permission';

@Component({
  selector: 'app-pm-checklist-exception-report',
  templateUrl: './pm-checklist-exception-report.component.html',
  styleUrls: ['./pm-checklist-exception-report.component.scss'],
})
export class PmChecklistExceptionReportComponent implements OnInit {
  // reuses the PM Dashboard permission until a dedicated menu item exists
  isAccess = ApplicationPermission.PM_DASHBOARD_ACCESS;

  languageDir = 'ltr';
  langObj: any = {};
  /** i18n section shared by the PM pages. */
  private readonly PAGE = 'PM';
  moduleId!: any;
  pageId!: any;

  plantSession: any;
  groupSession: any;
  userSession: any;

  HeaderForm: any;
  HeaderData: any = {
    StartDate: new Date(
      moment().subtract(1, 'M').set('hour', 6).set('minutes', 0).toString()
    ),
    EndDate: new Date(),
    Line: '',
  };

  public minDate: moment.Moment | undefined;
  public maxDate: moment.Moment | undefined;

  LineList!: any[];
  filteredLines: any;

  rowData: any[] = [];
  columnDefs!: any[];
  gridApi: any;
  gridColumnApi: any;

  // server side paging - the grid only ever holds the current page
  pageIndex = 0;
  pageSize = 50;
  pageSizeOptions = [25, 50, 100, 200];
  totalRows = 0;

  // the page runs itself once on open, using the default filters
  private autoLoaded = false;
  private autoLoadDone = false;

  /** English codes coming from SQL, translated for display only - never written back */
  private readonly CELL_CODES = [
    'Yes/No',
    'Value',
    'Answered No',
    'Below Minimum',
    'Above Maximum',
    'Fail',
    'Submitted',
    'In Progress',
  ];

  constructor(
    private fb: FormBuilder,
    private spinner: NgxSpinnerService,
    private toaster: ToastrService,
    private baseApi: BaseApi,
    private pmDashboardService: RMDashboardService,
    private rmconfigurationService: RMconfigurationService,
    private userConfigService: UserConfigurationsService,
    public translater: TranslaterService,
    public translate: TranslateService
  ) {
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.languageDir = event.lang === 'ar' ? 'rtl' : 'ltr';
      // repaint from the JSON files at once; getLangWords() repaints again
      this.langObj = null;
      this.applyLanguageToView();
      this.getLangWords();
    });

    this.columnDefs = [
      {
        headerName: 'Task Description',
        field: 'TaskDescription',
        minWidth: 160,
        wrapText: true,
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        // the occurrence id means nothing to a reader; kept in the column list so
        // it can be switched back on, but off the grid and out of the exports
        headerName: 'Task Number',
        field: 'TaskNumber',
        minWidth: 110,
        hide: true,
        filter: 'agSetColumnFilter',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Start Date',
        field: 'StartDate',
        minWidth: 130,
        valueFormatter: (params: any) =>
          params.value ? this.formatCellData(params.value, 'dateTime') : '',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'End Date',
        field: 'EndDate',
        minWidth: 130,
        valueFormatter: (params: any) =>
          params.value ? this.formatCellData(params.value, 'dateTime') : '',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Plant',
        field: 'Plant',
        minWidth: 110,
        filter: 'agSetColumnFilter',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Department',
        field: 'Department',
        minWidth: 120,
        filter: 'agSetColumnFilter',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Line',
        field: 'Line',
        minWidth: 110,
        filter: 'agSetColumnFilter',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Machine',
        field: 'Machine',
        minWidth: 110,
        filter: 'agSetColumnFilter',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Checklist',
        field: 'Checklist',
        minWidth: 140,
        filter: 'agSetColumnFilter',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Operator',
        field: 'Operator',
        minWidth: 130,
        filter: 'agSetColumnFilter',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Question',
        field: 'Question',
        minWidth: 180,
        wrapText: true,
        filter: 'agSetColumnFilter',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Question Type',
        field: 'QuestionType',
        minWidth: 120,
        filter: 'agSetColumnFilter',
        valueFormatter: this.translateCellValue.bind(this),
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Expected',
        field: 'Expected',
        minWidth: 110,
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Actual',
        field: 'Actual',
        minWidth: 110,
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Remarks',
        field: 'Remarks',
        minWidth: 140,
        wrapText: true,
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Exception Type',
        field: 'ExceptionType',
        minWidth: 140,
        filter: 'agSetColumnFilter',
        valueFormatter: this.translateCellValue.bind(this),
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Result',
        field: 'Result',
        minWidth: 100,
        valueFormatter: this.translateCellValue.bind(this),
        cellStyle: { color: '#d32f2f', fontWeight: '600' },
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Checklist Status',
        field: 'ChecklistStatus',
        minWidth: 130,
        filter: 'agSetColumnFilter',
        valueFormatter: this.translateCellValue.bind(this),
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Answered On',
        field: 'AnsweredOn',
        minWidth: 130,
        valueFormatter: (params: any) =>
          params.value ? this.formatCellData(params.value, 'dateTime') : '',
        headerValueGetter: this.localizeHeader.bind(this),
      },
      {
        headerName: 'Due Date',
        field: 'DueDate',
        minWidth: 120,
        valueFormatter: (params: any) =>
          params.value ? this.formatCellData(params.value, 'dateOnly') : '',
        headerValueGetter: this.localizeHeader.bind(this),
      },
    ];
  }

  defaultColDef = {
    flex: 1,
    resizable: true,
    sortable: true,
    filter: true,
  };

  ngOnInit(): void {
    this.plantSession = this.baseApi.getPlantFromStorage();
    this.groupSession = this.baseApi.getGroupFromStorage();
    this.userSession = this.baseApi.getUserSession();

    // every filter is mandatory - the report only runs on a fully specified window
    this.HeaderForm = this.fb.group({
      StartDate: ['', Validators.required],
      EndDate: ['', Validators.required],
      Line: ['', Validators.required],
    });

    this.getLangWords();
    this.getLine();

    // ngx-translate loads its json asynchronously and ag-Grid caches formatted
    // cells, so repaint headers AND cells once the file arrives
    this.translate.onTranslationChange.subscribe(() =>
      this.applyLanguageToView()
    );
  }

  getLine(): void {
    const key = {
      PlantCode: this.plantSession?.plantCode,
      userCode: this.userSession?.userCode,
    };
    this.spinner.show();
    this.rmconfigurationService.getLineData(key).subscribe(
      (data: any) => {
        this.spinner.hide();
        this.LineList = data.lstLine;
        this.filteredLines = this.LineList.slice();

        if (this.LineList && this.LineList.length) {
          const allLines = this.filteredLines.map((x: any) => x.PL_Id);
          this.HeaderForm.controls?.Line?.patchValue(['ALL', ...allLines]);
        }

        // first load only, and only once the mandatory filters are actually
        // filled - otherwise the page would greet the user with an error toast
        if (!this.autoLoaded && this.hasAllFilters) {
          this.autoLoaded = true;
          this.loadPage();
        }
      },
      (error: any) => {
        this.spinner.hide();
        this.toaster.error(
          this.t('Error', 'Error'),
          this.t('LineLoadFailed', 'Could not load the line list') +
            ' - ' +
            (error?.message ?? '')
        );

        // no line list means no line filter, which the proc treats as "all lines"
        if (!this.autoLoaded) {
          this.autoLoaded = true;
          this.loadPage();
        }
      }
    );
  }

  /** search button - always returns to the first page */
  getReport(): void {
    this.autoLoadDone = true;
    this.pageIndex = 0;
    this.loadPage();
  }

  onPageChange(event: any): void {
    if (!this.canSearch()) {
      return;
    }

    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadPage();
  }

  /** the ALL sentinel is a ui affordance, never a line id - strip it once, here */
  get selectedLineIds(): string {
    return (this.HeaderForm?.get('Line')?.value ?? '')
      .toString()
      .split(',')
      .filter((value: string) => value.trim() !== '' && value.trim() !== 'ALL')
      .join(',');
  }

  /** every filter is mandatory; drives the inline message and the disabled search */
  get hasAllFilters(): boolean {
    return (
      !!this.selectedLineIds &&
      !!this.HeaderData.StartDate &&
      !!this.HeaderData.EndDate
    );
  }

  /** true while the picked range reads backwards; drives the inline message and the disabled search */
  get isDateRangeReversed(): boolean {
    const start = this.HeaderData.StartDate;
    const end = this.HeaderData.EndDate;
    return !!start && !!end && moment(start).isAfter(moment(end));
  }

  /** grey out the days that would reverse the range - arrow fns, the picker calls them unbound */
  disabledStartDate = (current: Date): boolean =>
    !!current &&
    !!this.HeaderData.EndDate &&
    moment(current).startOf('day').isAfter(moment(this.HeaderData.EndDate).startOf('day'));

  disabledEndDate = (current: Date): boolean =>
    !!current &&
    !!this.HeaderData.StartDate &&
    moment(current).endOf('day').isBefore(moment(this.HeaderData.StartDate).startOf('day'));

  /**
   * Every filter is mandatory, and a reversed range returns nothing - which is
   * indistinguishable from a clean period. Both are refused here rather than
   * letting the user read an empty grid as good news.
   */
  private canSearch(): boolean {
    if (!this.hasAllFilters) {
      this.HeaderForm.markAllAsTouched();
      this.toaster.error(
        this.t('Error', 'Error'),
        this.t('PleaseFillAllRequiredFields', 'Please fill all required fields')
      );
      return false;
    }

    if (this.isDateRangeReversed) {
      this.toaster.error(
        this.t('Error', 'Error'),
        this.t(
          'StartDatemustbebeforeEndDate',
          'Start Date must be before End Date'
        )
      );
      return false;
    }

    return true;
  }

  /** builds the request; pageSize 0 means "every row", used by the exports */
  private buildRequest(pageSize: number, pageNumber: number): any {
    return {
      Groupcode: this.groupSession?.groupCode,
      Plantcode: this.plantSession?.plantCode,
      PLID: this.selectedLineIds,
      dateStart: this.HeaderData.StartDate ?? null,
      dateEnd: this.HeaderData.EndDate ?? null,
      PageNumber: pageNumber,
      PageSize: pageSize,
    };
  }

  private loadPage(): void {
    if (!this.canSearch()) {
      return;
    }

    if (!this.groupSession?.groupCode || !this.plantSession?.plantCode) {
      this.toaster.error(
        this.t('Error', 'Error'),
        this.t('SessionMissing', 'No plant or group in session')
      );
      return;
    }

    this.spinner.show();
    this.pmDashboardService
      .getPMChecklistExceptionReport(
        this.buildRequest(this.pageSize, this.pageIndex + 1)
      )
      .subscribe(
        (res: any) => {
          this.spinner.hide();
          this.rowData = res ?? [];
          // TotalRows is repeated on every row; zero rows means zero total
          this.totalRows = this.rowData.length ? this.rowData[0].TotalRows : 0;
          this.gridApi?.setRowData(this.rowData);
          this.gridColumnApi?.autoSizeAllColumns();

          // the toast is for a search the user asked for, not the automatic first load
          if (!this.totalRows && this.autoLoadDone) {
            this.toaster.info(
              this.t('NoData', 'No data'),
              this.t(
                'NoExceptionsFound',
                'No failed checklist items in this period'
              )
            );
          }
        },
        (error: any) => {
          this.spinner.hide();
          this.rowData = [];
          this.totalRows = 0;
          this.gridApi?.setRowData(this.rowData);
          this.toaster.error(
            this.t('Error', 'Error'),
            this.t('ReportLoadFailed', 'Could not load the exception report') +
              ' - ' +
              (error?.error ?? error?.message ?? '')
          );
        }
      );
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridColumnApi.autoSizeAllColumns();
  }

  excelExport(): void {
    this.exportAllRows('excel');
  }

  csvExport(): void {
    this.exportAllRows('csv');
  }

  /**
   * ag-Grid can only export rows it holds, and it holds one page. So pull every
   * row (PageSize 0), export, then put the current page back.
   */
  private exportAllRows(format: 'excel' | 'csv'): void {
    if (!this.canSearch()) {
      return;
    }

    this.spinner.show();
    this.pmDashboardService
      .getPMChecklistExceptionReport(this.buildRequest(0, 1))
      .subscribe(
        (res: any) => {
          const allRows = res ?? [];
          this.gridApi?.setRowData(allRows);

          const params = {
            fileName: this.t(
              'ChecklistExceptionReport',
              'Checklist Exception Report'
            ),
            processCellCallback: (cell: any) => {
              const value = this.exportCellValue(cell);
              // only Expected carries a "min - max" range, so only it is at risk
              const isExpected = cell.column?.getColId() === 'Expected';
              return format === 'csv' && isExpected
                ? this.keepAsTextInCsv(value)
                : value;
            },
          };

          if (format === 'excel') {
            this.gridApi?.exportDataAsExcel(params);
          } else {
            this.gridApi?.exportDataAsCsv(params);
          }

          // restore the page the user was looking at
          this.gridApi?.setRowData(this.rowData);
          this.spinner.hide();
        },
        (error: any) => {
          this.spinner.hide();
          this.toaster.error(
            this.t('Error', 'Error'),
            this.t('ExportFailed', 'Could not build the export') +
              ' - ' +
              (error?.error ?? error?.message ?? '')
          );
        }
      );
  }

  /**
   * A csv carries no types, so Excel guesses on open and reads the Expected
   * range "12 - 15" as 15 December. ="..." is the one form it keeps as literal
   * text. Only values that Excel would actually convert are wrapped, so the
   * file stays readable in anything that is not Excel.
   */
  private keepAsTextInCsv(value: any): any {
    const text = value == null ? '' : String(value);
    const looksLikeADateToExcel = /^\s*\d+\s*[-\/]\s*\d+(\s*[-\/]\s*\d+)?\s*$/.test(
      text
    );
    return looksLikeADateToExcel ? '="' + text + '"' : value;
  }

  /** export the value the user sees, not the raw code */
  private exportCellValue(params: any): any {
    const formatter = params.column?.getColDef()?.valueFormatter;
    return formatter ? formatter({ ...params, colDef: params.column.getColDef() }) : params.value;
  }

  formatCellData(value: any, formatType: string): string {
    return this.userConfigService.formatField(value, formatType);
  }

  /** display-only translation of the English codes the SP returns */
  translateCellValue(params: any): string {
    const value = params?.value;
    if (!value || this.CELL_CODES.indexOf(value) === -1) {
      return value ?? '';
    }
    return this.getTransalatedValue(value.replace(/[^A-Za-z0-9]/g, ''), value);
  }

  toggleAllSelectionLine(allSelectedLine: any): void {
    if (allSelectedLine.selected) {
      const allLines = this.filteredLines.map((x: any) => x.PL_Id);
      this.HeaderForm.controls.Line.patchValue(['ALL', ...allLines]);
    } else {
      this.HeaderForm.controls.Line.patchValue([]);
    }
  }

  tosslePerOneLine(allSelectedLine: any): void {
    if (allSelectedLine.selected) {
      allSelectedLine.deselect();
      return;
    }
    if (this.HeaderForm.controls.Line.value.length === this.filteredLines.length) {
      const allLines = this.filteredLines.map((x: any) => x.PL_Id);
      this.HeaderForm.controls.Line.patchValue(['ALL', ...allLines]);
    }
  }

  /**
   * Resolve a label:  langObj (database)  ->  "PM.<key>"  ->  flat "<key>"
   * ->  the English fallback passed by the caller.
   */
  public t(key: string, fallback: string): string {
    if (this.langObj && this.langObj[key]) {
      return this.langObj[key];
    }
    return this.lookup(this.PAGE + '.' + key) || this.lookup(key) || fallback;
  }

  private lookup(key: string): string | null {
    const value = this.translate.instant(key);
    if (typeof value === 'string' && value !== key) {
      return value;
    }
    if (value && typeof value === 'object' && typeof value.title === 'string') {
      return value.title;
    }
    return null;
  }

  public localizeHeader(parameters: any): string {
    const colDef = parameters && parameters.colDef ? parameters.colDef : {};
    return this.getTransalatedValue(colDef.field, colDef.headerName);
  }

  getTransalatedValue(value: string, headerLabel?: string): string {
    const compact = headerLabel ? headerLabel.replace(/\s+/g, '') : '';
    for (const candidate of [value, headerLabel, compact]) {
      if (candidate && this.langObj && this.langObj[candidate]) {
        return this.langObj[candidate];
      }
    }
    for (const candidate of [compact, headerLabel, value]) {
      if (!candidate) {
        continue;
      }
      const hit =
        this.lookup(this.PAGE + '.' + candidate) || this.lookup(candidate);
      if (hit) {
        return hit;
      }
    }
    return headerLabel ? headerLabel : value;
  }

  /** Repaint the grid without waiting for the database tag request. */
  private applyLanguageToView(): void {
    try {
      this.gridApi?.refreshHeader();
      this.gridApi?.refreshCells({ force: true });
    } catch (e) {
      // a destroyed grid must not break the language switch
    }
  }

  public getLangWords(): void {
    this.pageId = this.baseApi.getCurrentMenuID();
    const userMenus = this.baseApi.getUserPermissionsSession();
    this.moduleId = userMenus.find((x: any) => x.menuId === this.pageId)?.parentId;
    const key = {
      ModuleId: this.moduleId,
      PageId: this.pageId,
      LanguageId:
        this.translate.currentLang === 'en'
          ? 1
          : this.translate.currentLang === 'ar'
            ? 2
            : this.translate.currentLang === 'fr'
              ? 3
              : this.translate.currentLang === 'sp'
                ? 4
                : 1,
    };
    this.translater.getLangWords(key).subscribe(
      (res: any) => {
        const obj: any = {};
        res.tagList.map((elem: any) => {
          obj[elem.tagName] = elem.translatedValue;
        });
        this.langObj = obj;
        this.applyLanguageToView();
      },
      () => {
        this.applyLanguageToView();
      }
    );
  }
}
